/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Trends › Refusals by risk tier (docs/CONTRACT-fleet-governance.md sections 7.2 and 12.3): six rows, the five risk tiers
 * and "No tier" (caps and label policy), each with its total, the split by refusal kind and a sparkline; a row opens to
 * the top tools behind it. Each kind with audit rows behind it opens them; refusals blocked by risk and denials are
 * counted from the turn traces, which write no audit rows.
 */
import { Fragment, useState } from 'react';
import { CircleSlash } from 'lucide-react';
import type { RiskTier } from '../../lib/api';
import {
  REFUSAL_KINDS, REFUSAL_KIND_EVENTS, REFUSAL_KIND_LABELS, RISK_TIER_KEYS, trendsApi, trendsExportHref,
  type GovRefusalTier, type GovRefusalsAnswer, type RefusalKind,
} from '../../lib/trends';
import { EmptyState, Panel, RISK_CHIPS, SpecChip, Sparkline, StatusChip, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { ExportLink } from '../fleet/parts';
import { SectionError, ShowRows, useClientSort, type SectionProps } from './parts';

type TopTool = GovRefusalTier['topTools'][number];

function tierChip(tier: string) {
  return tier === 'none' ? <StatusChip tone="neutral" plain>No tier</StatusChip> : RISK_CHIPS[tier as RiskTier] ? <SpecChip spec={RISK_CHIPS[tier as RiskTier]} /> : <Tag>{tier}</Tag>;
}

/** The six rows in RISK_TIER_KEYS order, a tier the answer left out counted as 0. */
function ordered(answer: GovRefusalsAnswer): GovRefusalTier[] {
  const empty = Object.fromEntries(REFUSAL_KINDS.map((kind) => [kind, 0])) as Record<RefusalKind, number>;
  return RISK_TIER_KEYS.map((tier) => answer.tiers.find((row) => row.riskTier === tier) ?? { riskTier: tier, total: 0, byKind: empty, spark: [], topTools: [] });
}

export function TrendsRefusals({ query, filter, onShowRows }: SectionProps) {
  const { load, reload, reloading } = useLoad(() => trendsApi.refusals(query), `refusals|${JSON.stringify(query)}`);
  const answer = load.state === 'ready' ? load.data : null;
  const [open, setOpen] = useState<string | null>(null);
  const { rows, sort } = useClientSort(answer ? ordered(answer) : [], (row, key) => (key === 'total' ? row.total : row.byKind[key as RefusalKind] ?? 0));

  const show = (kind: RefusalKind, scope: { app?: string; tool?: string; tier?: string }) => {
    if (!answer) return;
    const where = scope.tool ? `${scope.app} · ${scope.tool}` : scope.tier ? 'every risk tier' : '';
    onShowRows({
      title: `Refusals · ${REFUSAL_KIND_LABELS[kind]}${where ? ` · ${where}` : ''}`,
      choices: REFUSAL_KIND_EVENTS[kind],
      app: scope.app ?? (filter.app || null),
      tool: scope.tool ?? null,
      range: { from: answer.range.from, to: answer.range.to },
    });
  };

  const columns: Array<Column<GovRefusalTier>> = [
    { key: 'tier', header: 'Risk tier', cell: (row) => tierChip(row.riskTier) },
    { key: 'total', header: 'Total', numeric: true, sortable: true, cell: (row) => count(row.total) },
    ...REFUSAL_KINDS.map((kind): Column<GovRefusalTier> => ({
      key: kind, header: REFUSAL_KIND_LABELS[kind], numeric: true, sortable: true, hideOnPhone: kind !== 'risk',
      cell: (row) => (row.byKind[kind] ? count(row.byKind[kind]) : <span className="ui-quiet">0</span>),
    })),
    { key: 'spark', header: 'Per day', hideOnPhone: true, cell: (row) => <Sparkline values={row.spark} label={`${row.riskTier === 'none' ? 'No tier' : row.riskTier}: refusals per day`} tone={row.total > 0 ? 'warn' : 'data'} /> },
  ];

  const toolColumns: Array<Column<TopTool>> = [
    { key: 'app', header: 'App', cell: (tool) => <Tag>{tool.app}</Tag> },
    { key: 'tool', header: 'Tool', mono: true, cell: (tool) => tool.tool },
    { key: 'kind', header: 'Kind', cell: (tool) => REFUSAL_KIND_LABELS[tool.kind] ?? tool.kind },
    { key: 'count', header: 'Refusals', numeric: true, cell: (tool) => count(tool.count) },
    {
      key: 'rows', header: <span className="ui-sr">Audit rows</span>, align: 'end',
      cell: (tool) => (REFUSAL_KIND_EVENTS[tool.kind]?.length ? <ShowRows onClick={() => show(tool.kind, { app: tool.app, tool: tool.tool })} /> : <span className="ui-quiet">from the turn traces</span>),
    },
  ];

  const detail = (row: GovRefusalTier) => {
    if (open !== row.riskTier) return null;
    const kinds = REFUSAL_KINDS.filter((kind) => row.byKind[kind] > 0 && REFUSAL_KIND_EVENTS[kind].length > 0);
    return (
      <div className="ui-form">
        {kinds.length ? (
          <span className="ui-toolbar" aria-label="Audit rows by kind">
            {kinds.map((kind) => <Fragment key={kind}><ShowRows label={`${REFUSAL_KIND_LABELS[kind]}: show audit rows`} onClick={() => show(kind, { tier: row.riskTier })} /></Fragment>)}
          </span>
        ) : null}
        {row.topTools.length ? (
          <div className="ui-table-box">
            <Table stacked label={`Top tools: ${row.riskTier === 'none' ? 'no tier' : row.riskTier}`} columns={toolColumns} rows={row.topTools} rowKey={(tool) => `${tool.app}|${tool.tool}|${tool.kind}`} stickyHeader={false} />
          </div>
        ) : <p className="ui-para ui-quiet">No tool behind these refusals in the range.</p>}
      </div>
    );
  };

  return (
    <Panel flush title="Refusals by risk tier" description="What Gyro refused, by the risk tier of the tool and the kind of refusal; a row opens its top tools" actions={<ExportLink href={trendsExportHref('refusals', filter)} />}>
      {load.state === 'failed' ? <div className="ui-panel-pad"><SectionError title="The refusals could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /></div> : (
        <Table
          label="Refusals by risk tier"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.riskTier}
          loading={load.state === 'loading'}
          skeletonRows={6}
          selectedKey={open}
          onRowClick={(row) => setOpen((current) => (current === row.riskTier ? null : row.riskTier))}
          detail={detail}
          sort={sort}
          empty={<EmptyState compact icon={CircleSlash} title="No refusals">Refusals appear here by risk tier.</EmptyState>}
        />
      )}
    </Panel>
  );
}
